import {
    Alert,
    Box,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { BACKEND_ROUTES } from "../../../backendRoutes";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { MODEL_PROPTYPES } from "../../../models";
import PropTypes from "prop-types";
import { camelCaseToTitleCase } from "../../../utils";
import { generateCommand } from "../../../utils/generateCommand";
import { getMissionLocalFolderPath } from "../../alignment-report/getMissionLocalFolderPath";
import useSWR from "swr";
import { useSnackbar } from "../../../hooks";

MetashapeUploadDialog.propTypes = {
    photogrammetryProcess: PropTypes.shape(MODEL_PROPTYPES.Process).isRequired,
};

export function MetashapeUploadDialog({ photogrammetryProcess }) {
    const { openSnackbar } = useSnackbar();

    const {
        data: pipelineInstance,
        error: pipelineInstanceFetchError,
        isLoading: pipelineInstanceIsLoading,
    } = useSWR(
        `${BACKEND_ROUTES.PIPELINE_INSTANCE}/${photogrammetryProcess.pipelineInstanceUuid}`
    );

    // Get missionUuid from the variables of translateParams
    const missionUuid =
        pipelineInstance?.translateParams?.variables?.missionUuid;

    const {
        data: mission,
        error: missionFetchError,
        isLoading: missionIsLoading,
    } = useSWR(missionUuid ? `${BACKEND_ROUTES.MISSION}/${missionUuid}` : null);

    const mergedFetchError = pipelineInstanceFetchError ?? missionFetchError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    const isLoading = pipelineInstanceIsLoading || missionIsLoading;
    if (isLoading)
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="10em"
            />
        );

    if (!mission)
        return <Alert severity="error">Cannot determine HCC mission.</Alert>;

    const outputFolder = getMissionLocalFolderPath(mission, pipelineInstance);

    const specs = outputFolder
        ? getMetashapeUploadSpecs(
              mission.uuid,
              outputFolder,
              photogrammetryProcess.uuid
          )
        : undefined;

    const command = specs ? generateCommand(specs, "post") : undefined;

    const onClickCopy = async () => {
        try {
            await navigator.clipboard.writeText(command);
        } catch (error) {
            openSnackbar(error.message, "error");
        }
    };

    return (
        <>
            <DialogTitle>Upload local metashape project</DialogTitle>
            <DialogContent>
                {command ? (
                    <Stack direction="column" spacing={1}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                defaultValue={command}
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        bgcolor: (theme) =>
                                            theme.palette.grey[210],
                                    },
                                }}
                                fullWidth
                            />
                            <Tooltip title="Copy">
                                <IconButton onClick={onClickCopy}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Divider />
                        <Box>
                            <Typography>
                                Command will upload (if available)
                            </Typography>
                            <List>
                                {Object.entries(specs.uploadResults)
                                    .toSorted((a, b) =>
                                        a[0]
                                            .toLowerCase()
                                            .localeCompare(b[0].toLowerCase())
                                    )
                                    .map(([name, value]) => (
                                        <ListItem key={name} disablePadding>
                                            <ListItemIcon>&bull;</ListItemIcon>
                                            <ListItemText
                                                primary={camelCaseToTitleCase(
                                                    name
                                                )}
                                                secondary={value.value}
                                            />
                                        </ListItem>
                                    ))}
                            </List>
                            <Typography>from</Typography>
                            <Box
                                sx={{
                                    overflowWrap: "anywhere",
                                    p: 1,
                                }}
                            >
                                {outputFolder}
                            </Box>
                        </Box>
                    </Stack>
                ) : (
                    <Alert severity="error">
                        Cannot determine local mission folder.
                    </Alert>
                )}
            </DialogContent>
        </>
    );
}

function getMetashapeUploadSpecs(missionUuid, outputFolder, processUuid) {
    return {
        missionUuid,
        outputFolder,
        uploadResults: {
            log: {
                type: "Log",
                value: "l1/photogram/process.log",
            },
            metashape: {
                type: "Folder",
                value: "l1/photogram/metashape",
            },
        },
        processUuid,
    };
}
